import { History, PackageCheck, Search, Settings, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { Menu, type MenuItem } from '../../../../components/Menu';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StackedCell } from '../../../../components/table/StackedCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { CancelDeliveryModal } from '../components/CancelDeliveryModal';
import { InspectDeliveryLineModal } from '../components/InspectDeliveryLineModal';
import { goodsReceiptsApi } from '../goodsReceiptApi';
import type { GoodsReceiptDetail, GoodsReceiptLine } from '../goodsReceiptTypes';
import { GOODS_RECEIPT_STATUS, goodsReceiptItemIdentity } from '../goodsReceiptTypes';

type TabKey = 'info' | 'lines';

/**
 * One supplier delivery — its facts, its lines, and the inspection that decides what becomes stock.
 *
 * <b>NO CARD CARRIES AN EDIT, and that is the module's shape rather than unfinished work.</b>
 * There is no update endpoint: what can still be done to the record is record-level — inspect a
 * line, register it, cancel it — so it sits in the header's cog, which is on screen for every
 * tab. That is `<ProfileCard>`'s second exemption read literally.
 */
/**
 * One page of lines, in the order a header asked for.
 *
 * The two sortable columns and nothing else — a key naming no column leaves the order the
 * server sent, which is the delivery's own, rather than inventing one.
 *
 * `useDataTable` cycles asc → desc → unsorted and spells the second as a leading `-`.
 */
function sortLines(lines: GoodsReceiptLine[], sort: string | null): GoodsReceiptLine[] {
  if (!sort) return lines;

  /*
    THE WIRE FORM IS `field:asc` / `field:desc` — `useDataTable.toggleSort`'s own spelling, and
    what `<DataTable>` splits on to draw `aria-sort`. It is NOT a leading `-`, which is the
    SERVER-side `ListQuery` convention; this panel sorts in memory and never reaches one.

    Parsed as a `-` it shipped broken for one run: `defaultSort` carries no direction, so the
    FIRST order came out right and every click after it fell through to the server's order while
    the header went on cycling asc → desc → none above it. **A sort that is wrong only after the
    first click looks exactly like a sort that works**, which is why this is proven by CLICKING
    the header and reading the rows back, never by loading the page once.
  */
  const [key, direction] = sort.split(':');
  const descending = direction === 'desc';

  const valueOf =
    key === 'itemName'
      ? (l: GoodsReceiptLine) => goodsReceiptItemIdentity(l.itemName, l.manufacturer).primary
      : key === 'sku'
        ? (l: GoodsReceiptLine) => l.sku
        : null;

  if (valueOf === null) return lines;

  /*
    A COPY, never `matched.sort(...)` in place. With no search term `matched` IS the `lines`
    array the record holds, so an in-place sort would reorder the page's own state behind the
    table — and the next render would read it already sorted.
  */
  return [...lines].sort((a, b) => {
    const d = valueOf(a).localeCompare(valueOf(b));

    return descending ? -d : d;
  });
}

export function GoodsReceiptProfilePage() {
  const { id = '' } = useParams<{ id: string }>();

  const [record, setRecord] = useState<GoodsReceiptDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  /*
    IT LANDS ON THE LINES, not on Info — product call, 05 Oct 2026, and the FIFTH member of the
    document-profile landing family after Repository, Inventory Audits, Purchase Requests and
    Purchase Orders. A reader opens a goods receipt to see WHAT ARRIVED and to inspect it; Info
    is right everywhere else precisely because those records are opened to check their own fields.

    `info` is still the FIRST tab DECLARED, so the strip's order and the check
    <ProfileHeaderShell> makes in development are untouched — only where the page lands.
  */
  const [tab, setTab] = useState<TabKey>('lines');

  const [inspecting, setInspecting] = useState<GoodsReceiptLine | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [running, setRunning] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setRecord(await goodsReceiptsApi.get(id, true, signal));
      } catch (caught) {
        if (signal?.aborted) return;

        setError(
          caught instanceof ApiError ? caught : new ApiError('That receipt could not be loaded.', 0),
        );
      }
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();

    void load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /*
    MEMOISED, AND THE PAGE DOES NOT RENDER WITHOUT IT.

    `record?.lines ?? []` builds a NEW EMPTY ARRAY on every render while the record is still
    loading, so `fetchPage` below took a new identity every render — and useDataTable's own
    contract says in its own words that "fetchPage must be stable (useCallback), or the effect
    re-runs on every render". Its effect called setRows with a fresh array, which re-rendered,
    which rebuilt lines, which rebuilt fetchPage: an infinite loop that React stopped with
    "Maximum update depth exceeded", leaving the profile BLANK on every open.

    It was pre-existing and independent of which tab the page lands on — useDataTable is called
    unconditionally, so landing on Info crashed exactly as landing on Lines did. It was found by
    OPENING THE PAGE: tsc, oxlint and test:conventions were all clean, and the one lint warning
    that pointed at it ("depends on lines, which changes every render") reads like dozens of
    pre-existing warnings beside it.

    THE CHECK FOR THE NEXT ONE: anything feeding useDataTable's fetchPage must be referentially
    stable across renders. A `?? []` fallback never is.
  */
  const lines = useMemo(() => record?.lines ?? [], [record]);

  /*
    SEARCHED, SORTED AND PAGED IN MEMORY, which is the 201 file's own exemption: the lines arrive
    WHOLE with the record, so the filter sees everything there is. What would end the exemption is
    paging them server-side.
  */
  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? lines.filter(
            (l) =>
              l.itemName.toLowerCase().includes(term) ||
              (l.manufacturer ?? '').toLowerCase().includes(term) ||
              l.sku.toLowerCase().includes(term) ||
              (l.batchNumber ?? '').toLowerCase().includes(term) ||
              (l.inspectionStatusLabel ?? '').toLowerCase().includes(term) ||
              l.serials.some((s) => s.serialNumber.toLowerCase().includes(term)),
          )
        : lines;

      /*
        SORTED IN MEMORY, because the section arrives WHOLE with the record — the 201 file's
        own exemption, and the same one the search above runs on. The filter and the sort both
        see every line there is, which is what the rule against client-side filtering asks.

        IT WAS IGNORED UNTIL 05 Oct 2026, and both sortable headers were dead controls: this
        callback read `query.search` and never `query.sort`, so clicking a header moved the
        indicator and reordered nothing. Nothing failed — a one-line delivery looks identical
        sorted or not — which is why it survived. Found while stacking the ITEM cell, where
        "what does this column order by" stopped being answerable by looking at it.

        THE ITEM COLUMN ORDERS BY WHAT IT LEADS WITH, through `goodsReceiptItemIdentity`, so
        the rows read down in the order of the line a reader is scanning. Ordering on the TYPE
        while the manufacturer is on top would be a column sorted by its second line.
      */
      const sorted = sortLines(matched, query.sort ?? null);

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [lines],
  );

  const state = useDataTable<GoodsReceiptLine>({ fetchPage, pageSize: 25, defaultSort: 'itemName:asc' });

  async function register() {
    setRunning(true);
    setError(null);

    try {
      setRecord(await goodsReceiptsApi.register(id));
      setRegistering(false);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The delivery could not be registered.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  if (error && !record) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb segments={['Logistics', 'Inventory', 'Goods Receipts']} />
        <p role="alert" className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error.message}
        </p>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb segments={['Logistics', 'Inventory', 'Goods Receipts']} />
        <div className="m-4 h-32 animate-pulse rounded-lg bg-slate-100" />
      </div>
    );
  }

  const isOpen = record.status === GOODS_RECEIPT_STATUS.pendingInspection;
  /*
    `== null`, NOT `=== null`, and this line is why the rule exists.

    `Program.cs` sets `DefaultIgnoreCondition = WhenWritingNull`, so an uninspected line's
    `quantityAccepted` is OMITTED from the JSON entirely and arrives as `undefined` —
    `undefined === null` is false. With the strict comparison this counted ZERO uninspected
    lines on a delivery nobody had looked at, so the header read "0 awaiting inspection" and
    the banner said "Inspected and ready to register" on a receipt with no verdicts at all.

    TypeScript cannot see it: the DTO declares `int?` and this type declares `number | null`,
    which is what the server PROMISES rather than what the wire SENDS. `tsc`, the lint and the
    convention scan were all clean. Only opening the page found it.
  */
  const uninspected = record.lines.filter((l) => l.quantityAccepted == null).length;

  const actions: MenuItem[] = [
    /*
      ABSENT rather than disabled once the delivery has ended — a registered or cancelled receipt
      has no register left to press, so the entry does not apply to that record at all. Disabled
      for STATE is for the case that is still reachable, which is why the uninspected one below is.
    */
    ...(isOpen
      ? [
          {
            key: 'register',
            label: 'Close delivery',
            icon: <PackageCheck className="h-4 w-4" aria-hidden="true" />,
            permission: 'goods-receipts.register',
            disabledReason:
              uninspected > 0
                ? `${uninspected} of ${record.lines.length} lines have not been inspected yet.`
                : null,
            onClick: () => setRegistering(true),
          } satisfies MenuItem,
          {
            key: 'cancel',
            label: 'Cancel delivery',
            icon: <XCircle className="h-4 w-4" aria-hidden="true" />,
            permission: 'goods-receipts.register',
            onClick: () => setCancelling(true),
          } satisfies MenuItem,
        ]
      : []),
    {
      key: 'history',
      label: 'History Logs',
      icon: <History className="h-4 w-4" aria-hidden="true" />,
      permission: 'audit-logs.read',
      onClick: () => setHistoryOpen(true),
    },
  ];

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb
        segments={[
          'Logistics',
          'Inventory',
          { label: 'Goods Receipts', to: '/inventory/goods-receipts' },
          record.receiptNumber,
        ]}
      />

      <ProfileHeaderShell<TabKey>
        id="goods-receipt-profile"
        header={
          <ProfileHeader
            id="goods-receipt-profile-header"
            icon={<PackageCheck className="h-6 w-6" aria-hidden="true" />}
            title={record.receiptNumber}
            /*
              THE SOURCE AND ITS COUNTERPARTY, which every receipt has — where the order number
              and the supplier are null on two of the three sources. A meta line that read as two
              gaps on a donation would say less than it costs.
            */
            meta={[
              record.sourceLabel,

              /*
                THE ORDER NUMBER, from 05 Oct 2026 — product call, in the reader's own words:
                *display the PO number as well*. It sits between the source and the
                counterparty, which is the order the register's own anchor reads in
                (`GR-… — PO-… — supplier`), so the two surfaces name a delivery the same way.

                A DONATION and a TRANSFER name no order, so the entry is simply absent rather
                than an em dash: a meta line states the facts a record HAS.
              */
              ...(record.orderNumber ? [record.orderNumber] : []),
              record.counterparty,
              `Delivered ${formatDateOnly(record.deliveredOn)}`,
            ]}
            secondary={[
              `${record.lines.length} ${record.lines.length === 1 ? 'line' : 'lines'}`,
              `${record.lines.reduce((sum, l) => sum + l.quantityReceived, 0)} received`,
              record.isFullyInspected
                ? `${record.lines.reduce((sum, l) => sum + (l.quantityAccepted ?? 0), 0)} accepted`
                : `${uninspected} awaiting inspection`,
              record.totalValue == null ? null : formatMoney(record.totalValue),
              record.invoiceNumber ? `Invoice ${record.invoiceNumber}` : null,
            ]}
            status={
              <StatusText tone={record.isActive ? 'active' : 'inactive'}>
                {record.isActive ? record.statusLabel : 'Deleted'}
              </StatusText>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            actions={
              // THE TRIGGER IS REQUIRED. `<Menu>` renders NO default one — without it the cog
              // has nothing to click, and Register and Cancel become endpoints with a key, a
              // test and no control that reaches them. That shipped for one revision here and
              // is exactly the shape non-negotiable 2 refuses; it was found by driving the
              // page, because nothing about a menu with no trigger looks wrong in the markup.
              <Menu
                id="goods-receipt-profile-settings"
                items={actions}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    id="goods-receipt-profile-settings-trigger"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Receipt settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          <Banner
            record={record}
            uninspected={uninspected}
            /*
              IT OPENS THE FIRST UNINSPECTED LINE, and `setTab` alone is what it did until
              05 Oct 2026 — which stopped doing anything the moment this profile started
              LANDING on Lines: the button set the tab the reader was already on, so
              "Inspect lines" was a control that did nothing. Found by a reader pressing it.

              THE CHECK FOR THE NEXT LANDING CHANGE: every control that NAVIGATES to a tab is
              dead once the page opens on it. Either it does the work instead, or it goes.

              `== null`, never `===`: the server omits a null property entirely, so an
              uninspected line's `quantityAccepted` arrives `undefined` — the same trap this
              file already documents where `uninspected` is counted.
            */
            onInspect={() => {
              setTab('lines');

              const next = record.lines.find((l) => l.quantityAccepted == null);

              if (next) setInspecting(next);
            }}
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'lines',
            /*
              ONE NUMBER, through `<CountBadge>` — never inline `(N)` parens, which is the
              folder-tab rule stated in its own section and which this was the LAST call site in
              the product still breaking (05 Oct 2026). A zero renders, which is what tells a
              reader the tab is empty before they click it.
            */
            label: (
              <>
                Lines <CountBadge count={record.lines.length} />
              </>
            ),
            permission: 'goods-receipts.read',
          },
        ]}
        activeTab={tab}
        onTabChange={setTab}
        panel={tab === 'lines' ? 'bleed' : 'cards'}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard id="goods-receipt-profile-delivery" title="Delivery">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Receipt', value: record.receiptNumber, mono: true },
                  { label: 'Source', value: record.sourceLabel },

                  /*
                    EVERY ROW RENDERS, whatever the source, which is this table's own rule: a
                    reader cannot tell a missing fact from one that does not apply, so the two
                    that do not apply draw an em dash rather than vanishing and making the card
                    a different shape per receipt.
                  */
                  { label: 'Purchase order', value: record.orderNumber, mono: true },
                  { label: 'Supplier', value: record.supplierName },
                  { label: 'Donor', value: record.donorName },
                  { label: 'From store', value: record.sourceFacilityName },
                  { label: 'Delivered on', value: formatDateOnly(record.deliveredOn), mono: true },
                  { label: 'Stage', value: record.statusLabel },
                  {
                    label: 'Total value',
                    value: record.totalValue == null ? null : formatMoney(record.totalValue),
                    mono: true,
                  },
                  /*
                    IT READS BESIDE Total value RATHER THAN UNDER THE INVOICE HEADING, and that
                    placement is the whole of what keeps it honest: this figure is the WHOLE
                    order's, so on a partial delivery it is not what the supplier billed. Beside
                    what the accepted goods on THIS receipt came to, the gap between the two is
                    on screen; filed as an "invoice amount" it would have asserted a bill nobody
                    issued. Em dash on a donation and a transfer, which name no order.
                  */
                  {
                    label: 'Order total',
                    value:
                      record.orderTotalAmount == null ? null : formatMoney(record.orderTotalAmount),
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="goods-receipt-profile-invoice" title="Invoice">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Invoice #', value: record.invoiceNumber, mono: true },
                  {
                    label: 'Invoice date',
                    value: record.invoiceDate ? formatDateOnly(record.invoiceDate) : null,
                    mono: true,
                  },
                  { label: 'Notes', value: record.notes },
                  { label: 'Cancellation reason', value: record.cancellationReason },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="goods-receipt-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: record.createdByName },
                  { label: 'Created', value: formatDateTime(record.createdAt), mono: true },
                  { label: 'Updated by', value: record.updatedByName },
                  {
                    label: 'Latest update',
                    value: record.updatedAt ? formatDateTime(record.updatedAt) : null,
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : (
          /*
            THE `p-4` IS THE CALLER'S, because a `bleed` profile panel gives its child no inset.
            Without it the search group's border sits on the panel's border and the table runs
            edge to edge, so the two read as ONE CONTROL THAT HAS COME APART — which is the
            doubled-1px-seam failure the never-touch rule names, and what this tab looked like
            until 05 Oct 2026. Product call, in the reader's own words: *in the GR profile, line
            items, apply this search + table layout*, over a screenshot of the budget plan's PPA
            roster, which carries exactly this wrapper and whose own comment states the rule.

            <b>IT IS THE WRAPPER, NOT THE PANEL</b>: the inset belongs to the region hosting the
            child rather than to either neighbour, or two components each contributing half a gap
            double it wherever a third joins them.

            `h-full` and `flex-col` are what keep `fill` working through it — the panel
            distributes the height it is GIVEN, so a wrapper that did not pass the full height on
            would leave the pager floating under a short table.
          */
          <div className="flex h-full min-w-0 flex-col p-4">
            <DataTablePanel<GoodsReceiptLine>
              id="goods-receipt-lines"
              state={state}
              columns={LINE_COLUMNS}
              rowKey={(line) => line.id}
              searchPlaceholder="Search item, SKU, batch, serial or status"
              emptyMessage="Nothing is on this delivery."
              emptySearchMessage="No line on this delivery matches that search."
              minWidth={1240}
              fill
              selectable={false}

              rowActions={(line) =>
                isOpen
                  ? [
                      {
                        key: `inspect-${line.id}`,
                        label: line.quantityAccepted == null ? 'Inspect' : 'Re-inspect',
                        icon: <Search className="h-4 w-4" aria-hidden="true" />,
                        permission: 'goods-receipts.inspect',

                        /*
                          DISABLED FOR STATE once the stock exists, with the way out in the
                          sentence. The server has refused this since stock moved to the
                          inspection on 05 Oct 2026 — the ledger is append-only, so a changed
                          verdict needs a compensating adjustment — but the entry went on
                          offering itself, so the reader met the rule as a 400 in the console
                          instead of as a reason on the control. A server guard is not an
                          affordance: both are owed.
                        */
                        disabledReason: line.isStocked
                          ? "This line's stock already exists, so its verdict cannot be changed. Correct it with a stock adjustment against the item."
                          : null,
                        onClick: () => setInspecting(line),
                      },
                    ]
                  : []
              }
              otherInfo={(line) => line}
            />
          </div>
        )}
      </ProfileHeaderShell>

      {inspecting ? (
        <InspectDeliveryLineModal
          open
          receiptId={record.id}
          receiptNumber={record.receiptNumber}
          line={inspecting}
          onClose={() => setInspecting(null)}
          onSaved={(saved) => {
            setInspecting(null);
            setRecord(saved);
          }}
        />
      ) : null}

      {cancelling ? (
        <CancelDeliveryModal
          open
          receiptId={record.id}
          receiptNumber={record.receiptNumber}
          onClose={() => setCancelling(false)}
          onSaved={(saved) => {
            setCancelling(false);
            setRecord(saved);
          }}
        />
      ) : null}

      {registering ? (
        <ConfirmDialog
          open
          onClose={() => setRegistering(false)}
          onConfirm={() => void register()}
          title="Close this delivery"
          requireConfirm
          loading={running}
          id="goods-receipt-register-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                <b>The stock already exists.</b> Each line became a lot — or, where the item is
                tracked by serial, one unit per accepted serial — the moment that line was
                inspected, with a receipt written to the stock ledger.
              </p>

              <p>
                The purchase order is closed by what was <b>accepted</b>, not by what arrived. Any
                rejected quantity leaves its line open for the supplier to re-deliver against.
              </p>

              <p>
                It cannot be undone. A registered delivery is not reversed — stock that has landed
                comes back out through a stock adjustment with its own reason.
              </p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      {historyOpen ? (
        <HistoryLogsModal
          open
          onClose={() => setHistoryOpen(false)}
          module="goods-receipts"
          entityId={id}
        />
      ) : null}
    </div>
  );
}

/**
 * The banner states the stage and offers the one thing waiting to be done.
 *
 * It renders for EVERY state, which is `<ProfileBanner>`'s own rule: a strip that appears only
 * when something needs doing is one the reader learns to skip.
 */
function Banner({
  record,
  uninspected,
  onInspect,
}: {
  record: GoodsReceiptDetail;
  uninspected: number;
  onInspect: () => void;
}) {
  if (record.status === GOODS_RECEIPT_STATUS.cancelled) {
    return (
      <NextActionAlert
        tone="slate"
        icon={<XCircle className="h-4 w-4" aria-hidden="true" />}
        title="Delivery cancelled"
        description={
          record.cancellationReason ??
          'This delivery was abandoned before inspection. Nothing was created, so nothing was unwound.'
        }
      />
    );
  }

  if (record.status === GOODS_RECEIPT_STATUS.received) {
    return (
      <NextActionAlert
        tone="emerald"
        icon={<PackageCheck className="h-4 w-4" aria-hidden="true" />}
        title="Registered into stock"
        description="The accepted quantities are stock and the purchase order has been closed by them. Correcting what is on the shelf now is a stock adjustment."
      />
    );
  }

  if (uninspected > 0) {
    return (
      <NextActionAlert
        tone="amber"
        icon={<Search className="h-4 w-4" aria-hidden="true" />}
        title={`${uninspected} of ${record.lines.length} lines await inspection`}
        description="Inspect each line — what passes becomes stock the moment you submit it, and what is rejected never does: it is in no stock figure and is tagged for return to the supplier."
        actionLabel="Inspect lines"
        onAction={onInspect}
        actionPermission="goods-receipts.inspect"
      />
    );
  }

  return (
    <NextActionAlert
      tone="blue"
      icon={<PackageCheck className="h-4 w-4" aria-hidden="true" />}
      title="Inspected and in stock"
      description="Every line has a verdict and its stock already exists. Closing the delivery is what shuts the order's lines, by what was accepted."
    />
  );
}

/**
 * A totals cell on the lines table.
 *
 * Product call, 05 Oct 2026, in the reader's own words: *add sticky footer in the bottom of the
 * table as well.* One column declaring a `footer` turns the row on for the whole table, and
 * `<DataTable>` pins it to the bottom edge and stretches a spacer row to hold it there — a total
 * is what the rows add up to, so a scroll that carried it off would hide the one figure a grid of
 * figures is opened for.
 *
 * <b>It matches the CELLS above it</b> — `mono numeric`, so a zero dims exactly as a zero in the
 * column does. Not bold: the tint and the frame are the treatment.
 */
function sumCell(total: number) {
  return <TextCell value={String(total)} mono numeric />;
}

/**
 * The official inspection's totals — <b>an em dash while ANY line is still uninspected.</b>
 *
 * This is `GoodsReceiptListItem.TotalAccepted`'s own rule read one surface along: a partial sum
 * reads as a finished figure, so a delivery with one line ruled on and five outstanding must not
 * print a total that looks like the verdict on all six. Null is "nobody has looked"; zero is a
 * finding, and collapsing them would report an untouched delivery as fully rejected.
 *
 * <b>TOTAL IT OR REFUSE IT</b> — the rule the two columns beside it answer the other way. The
 * dock's four counts are never null, so they always sum.
 */
function verdictTotal(lines: GoodsReceiptLine[], pick: (line: GoodsReceiptLine) => number | null) {
  if (lines.length === 0 || lines.some((line) => pick(line) == null)) {
    return <TextCell value="—" mono numeric />;
  }

  return sumCell(lines.reduce((n, line) => n + (pick(line) ?? 0), 0));
}

/**
 * The delivery's VALUE — what the priced lines add up to.
 *
 * Product call, 05 Oct 2026, in the reader's own words: *in the gr profile, add a standard footer
 * for total.* The quantity columns had footers and the one money column did not, so the figure a
 * reader opens a delivery to find was the one the totals row could not state.
 *
 * <b>IT COUNTS ONLY THE PRICED LINES, and that is NOT the refusal `verdictTotal` makes.</b> The
 * two nulls mean different things. `quantityAccepted` is null because <i>nobody has inspected
 * yet</i> — an answer that is coming — so a partial sum would read as a finished verdict. A null
 * `lineTotal` is a line with <i>no price at all</i>: a donated article was not bought, and no
 * later act gives it a cost. Refusing on one would leave a part-priced delivery unable to state
 * what the rest was worth.
 *
 * That is `StockLevelListItem.Investment`'s own call, one module along, and the SALN totals' —
 * an absent figure adds nothing rather than voiding the sum.
 *
 * <b>An em dash only when NOTHING is priced</b>, which is the honest answer for a donation: zero
 * would be a figure somebody declared.
 */
function moneyTotal(lines: GoodsReceiptLine[]) {
  const priced = lines.filter((line) => line.lineTotal != null);

  if (priced.length === 0) return <TextCell value="—" mono numeric />;

  return (
    <TextCell
      value={formatMoney(priced.reduce((n, line) => n + (line.lineTotal ?? 0), 0))}
      mono
      numeric
    />
  );
}

const LINE_COLUMNS: DataColumn<GoodsReceiptLine>[] = [
  {
    key: 'itemName',
    header: 'Item',
    width: 260,
    sortable: true,
    render: (line) => {
      const { primary, secondary } = goodsReceiptItemIdentity(line.itemName, line.manufacturer);

      /*
        A STACKED CELL, and it is legal HERE: the dev error is scoped by
        `IndexTableSurfaceContext`, which only `<IndexTable>` provides — a `<DataTablePanel>`
        does not, and three panels already stack for the same reason.
      */
      return <StackedCell primary={primary} secondary={secondary} />;
    },
  },
  {
    key: 'sku',
    header: 'SKU',
    width: 160,
    sortable: true,
    render: (line) => <TextCell value={line.sku} mono />,
  },
  {
    key: 'storageFacilityName',
    header: 'Store',
    width: 170,
    render: (line) => <TextCell value={line.storageFacilityName ?? '—'} />,
  },
  {
    key: 'batchNumber',
    header: 'Batch',
    width: 150,
    render: (line) => <TextCell value={line.batchNumber ?? '—'} mono />,
  },
  {
    key: 'expiryDate',
    header: 'Expiry',
    width: 130,
    render: (line) => (
      <TextCell value={line.expiryDate ? formatDateOnly(line.expiryDate) : '—'} mono />
    ),
  },
  {
    key: 'quantityReceived',
    header: 'Received',
    width: 110,
    align: 'right',
    render: (line) => <TextCell value={String(line.quantityReceived)} mono numeric />,
    footer: (lines) => sumCell(lines.reduce((n, l) => n + l.quantityReceived, 0)),
  },
  /*
    TWO BANDS, because there are TWO INSPECTIONS — product call, 01 Oct 2026, in the reader's own
    words: *make 2 inspections, from the GR and from the official inspection.*

    The first four are what the RECEIVING CLERK found as the boxes were opened; the two after
    them are what the OFFICIAL INSPECTION decided. They are deliberately drawn side by side
    rather than one overwriting the other: a dock that found two damaged and an inspector who
    rejected five is a finding about the delivery, and a single pair of columns could not state
    it.

    `DataColumn.group` collects runs of ADJACENT columns sharing a label, so the order here is
    what draws the bands — a column slipped between the two runs would split one of them in half.
  */
  {
    key: 'quantityGood',
    header: 'Good',
    group: 'Receiving',
    width: 100,
    align: 'right',
    render: (line) => <TextCell value={String(line.quantityGood)} mono numeric />,
    footer: (lines) => sumCell(lines.reduce((n, l) => n + l.quantityGood, 0)),
  },
  {
    key: 'quantityExpired',
    header: 'Expired',
    group: 'Receiving',
    width: 100,
    align: 'right',
    render: (line) => <TextCell value={String(line.quantityExpired)} mono numeric />,
    footer: (lines) => sumCell(lines.reduce((n, l) => n + l.quantityExpired, 0)),
  },
  {
    key: 'quantityDamaged',
    header: 'Damaged',
    group: 'Receiving',
    width: 100,
    align: 'right',
    render: (line) => <TextCell value={String(line.quantityDamaged)} mono numeric />,
    footer: (lines) => sumCell(lines.reduce((n, l) => n + l.quantityDamaged, 0)),
  },
  {
    key: 'quantityWrongItem',
    header: 'Wrong item',
    group: 'Receiving',
    width: 110,
    align: 'right',
    render: (line) => <TextCell value={String(line.quantityWrongItem)} mono numeric />,
    footer: (lines) => sumCell(lines.reduce((n, l) => n + l.quantityWrongItem, 0)),
  },
  {
    /*
      AN EM DASH UNTIL INSPECTED, never a zero. Null is "nobody has looked"; zero is a finding —
      somebody looked and rejected the lot. Collapsing them would report an untouched line as
      fully rejected.

      That distinction is why the dock's four columns beside these are NOT nullable: zero there
      means the clerk found nothing wrong, which is an answer, where null here means nobody has
      been asked yet.
    */
    key: 'quantityAccepted',
    header: 'Accepted',
    group: 'Official inspection',
    width: 110,
    align: 'right',
    render: (line) => (
      <TextCell
        value={line.quantityAccepted == null ? '—' : String(line.quantityAccepted)}
        mono
        numeric
      />
    ),
    footer: (lines) => verdictTotal(lines, (l) => l.quantityAccepted),
  },
  {
    key: 'quantityRejected',
    header: 'Rejected',
    group: 'Official inspection',
    width: 110,
    align: 'right',
    render: (line) => (
      <TextCell
        value={line.quantityRejected == null ? '—' : String(line.quantityRejected)}
        mono
        numeric
      />
    ),
    footer: (lines) => verdictTotal(lines, (l) => l.quantityRejected),
  },
  {
    /*
      What stays OPEN on the order line once this receipt is registered — a fully-received line
      reads 0. It counts the ACCEPTED quantity, so a rejected delivery leaves the balance where
      it was.
    */
    key: 'balanceAfter',
    header: 'Balance',
    width: 110,
    align: 'right',
    /*
      AN EM DASH, never `String(null)` — which prints the literal "null" in the cell. A donation
      and a transfer answer no order, so there is nothing outstanding to be short of, and a ZERO
      would read as a line fully delivered. The DTO has carried it nullable since the three
      sources landed; this render had not caught up.
    */
    render: (line) =>
      line.balanceAfter == null ? (
        <TextCell value="—" mono numeric />
      ) : (
        <TextCell value={String(line.balanceAfter)} mono numeric />
      ),
  },
  {
    /*
      THE VERDICT, where this was a free-text rejection reason — product call, 05 Oct 2026. It
      is one of five closed values now, and on a PARTIAL line it describes the REJECTED part,
      which is what the column was always for.
    */
    key: 'inspectionStatus',
    header: 'Item status',
    width: 200,
    summary: true,
    render: (line) => <TextCell value={line.inspectionStatusLabel ?? '—'} />,
  },
  {
    /*
      TAGGED FOR RETURN TO SUPPLIER, and it is DERIVED rather than stored — the rejected
      quantity and its verdict ARE the tag. Rejected goods never become stock, so no ledger
      line can carry their return: StockMovementType 2 is reserved for a ReturnToSupplier
      RECORD this product does not have, and spending that number on a flag would spend it
      before anything can write it.

      An EM DASH rather than "No" on a line with nothing rejected: there is no return pending,
      which is an absence rather than a state to report on every clean row.
    */
    key: 'awaitsReturnToSupplier',
    header: 'Return',
    width: 150,
    render: (line) =>
      line.awaitsReturnToSupplier ? (
        <TextCell value={`Awaiting return (${line.quantityRejected ?? 0})`} />
      ) : (
        <TextCell value="—" />
      ),
  },
  {
    // The delivery note the wizard collects, and the inspection remark recorded against it.
    // Both were on the row and drawn by nothing until the audit found them.
    key: 'notes',
    header: 'Delivery note',
    width: 200,
    render: (line) => <TextCell value={line.notes ?? '—'} />,
  },
  {
    key: 'inspectionNotes',
    header: 'Inspection note',
    width: 200,
    render: (line) => <TextCell value={line.inspectionNotes ?? '—'} />,
  },
  {
    key: 'lineTotal',
    header: 'Line total',
    width: 140,
    align: 'right',
    render: (line) => (
      <TextCell value={line.lineTotal == null ? '—' : formatMoney(line.lineTotal)} mono numeric />
    ),
    footer: moneyTotal,
  },
];
